import { useEffect, useState } from 'react';
import Modal from './Modal';
import { spinnerRing } from '../utils/bookingUtils';
import { useConfig } from '../context/ConfigContext';
import { usePosSession } from '../context/PosSessionContext';
import { savePosClientSettings, posProductPageSize } from '../utils/posUtil';
import { describeWriteError } from '../utils/firebaseErrorUtil';
import { posCardSize, parsePosCardDimension } from '../utils/posCardSizeUtil';
import {
  POS_CARD_WIDTH_KEY,
  POS_CARD_HEIGHT_KEY,
  POS_CARD_WIDTH_MIN,
  POS_CARD_WIDTH_MAX,
  POS_CARD_HEIGHT_MIN,
  POS_CARD_HEIGHT_MAX,
  POS_CARD_NAME_FONT_KEY,
  POS_CARD_PRICE_FONT_KEY,
  POS_CARD_DETAIL_FONT_KEY,
  POS_CARD_FONT_MIN,
  POS_CARD_FONT_MAX,
  POS_PRODUCT_PAGE_SIZE_KEY,
  POS_PRODUCT_PAGE_SIZE_MIN,
  POS_PRODUCT_PAGE_SIZE_MAX,
} from '../constants/posConst';
import '../css/Pos.css';

/**
 * Product Settings — how this client's screens list its products.
 *
 * PER CLIENT, like the theme beside it, and written to the same node. One club
 * runs its till on a 10-inch counter tablet and wants twelve products a page;
 * another has a desk monitor and wants the lot. Neither should need an
 * administrator, and neither should be able to change it for the other.
 *
 * @returns {JSX.Element}
 */
function PosProductSettingsForm() {
  const { config, clientId, loading } = useConfig();
  const { staffEmail } = usePosSession();
  // Kept as a STRING so the field can be emptied while it is being retyped —
  // and because empty is a real setting here ("show every product"), not a
  // half-finished one.
  const [pageSize, setPageSize] = useState(null);
  // Product card size, as typed. Blank = automatic.
  const [cardWidth, setCardWidth] = useState('');
  const [cardHeight, setCardHeight] = useState('');
  // Card font sizes, as typed. Blank = the stylesheet's own size.
  const [nameFont, setNameFont] = useState('');
  const [priceFont, setPriceFont] = useState('');
  const [detailFont, setDetailFont] = useState('');
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState(null);

  // Seeded once rather than bound live: bound, the field would be rewritten
  // under the cursor by the value it is editing.
  useEffect(() => {
    if (loading || pageSize !== null) return;
    const stored = posProductPageSize(config);
    setPageSize(stored > 0 ? String(stored) : '');
    const size = posCardSize(config);
    setCardWidth(size.width > 0 ? String(size.width) : '');
    setCardHeight(size.height > 0 ? String(size.height) : '');
    setNameFont(size.nameFont > 0 ? String(size.nameFont) : '');
    setPriceFont(size.priceFont > 0 ? String(size.priceFont) : '');
    setDetailFont(size.detailFont > 0 ? String(size.detailFont) : '');
  }, [loading, pageSize, config]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const saved = await savePosClientSettings(
        clientId,
        {
          [POS_PRODUCT_PAGE_SIZE_KEY]: pageSize,
          [POS_CARD_WIDTH_KEY]: cardWidth,
          [POS_CARD_HEIGHT_KEY]: cardHeight,
          [POS_CARD_NAME_FONT_KEY]: nameFont,
          [POS_CARD_PRICE_FONT_KEY]: priceFont,
          [POS_CARD_DETAIL_FONT_KEY]: detailFont,
        },
        staffEmail,
      );
      const size = saved[POS_PRODUCT_PAGE_SIZE_KEY];
      setModal({
        type: 'success',
        title: 'Settings saved',
        message: size === 0
          ? 'Products and Inventory now list every product, with no paging.'
          : `Products and Inventory now show ${size} product${size === 1 ? '' : 's'} at a time.`,
      });
    } catch (error) {
      console.error('Saving product settings failed:', error);
      setModal({
        type: 'error',
        title: 'Could not save',
        message: describeWriteError(error, {
          what: 'these settings',
          path: `posTenants/${clientId}/config`,
        }),
      });
    } finally {
      setSaving(false);
    }
  };

  /** Drops the unsaved edit by re-seeding from what is stored. @returns {void} */
  const handleReset = () => setPageSize(null);

  if (loading || pageSize === null) {
    return <div className="pos-loading">{spinnerRing('Loading settings…')}</div>;
  }

  const typed = pageSize.trim();
  const asNumber = Number(typed);
  const pageSizeValid = typed === ''
    || (Number.isInteger(asNumber) && asNumber >= POS_PRODUCT_PAGE_SIZE_MIN && asNumber <= POS_PRODUCT_PAGE_SIZE_MAX);
  const widthValid = parsePosCardDimension(cardWidth, POS_CARD_WIDTH_MIN, POS_CARD_WIDTH_MAX).ok;
  const heightValid = parsePosCardDimension(cardHeight, POS_CARD_HEIGHT_MIN, POS_CARD_HEIGHT_MAX).ok;
  const fontValid = [nameFont, priceFont, detailFont]
    .every((value) => parsePosCardDimension(value, POS_CARD_FONT_MIN, POS_CARD_FONT_MAX).ok);
  const valid = pageSizeValid && widthValid && heightValid && fontValid;

  // The three font fields share one layout, so they are described once.
  const fontFields = [
    { label: 'Name (px)', value: nameFont, set: setNameFont, placeholder: 'Auto' },
    { label: 'Price (px)', value: priceFont, set: setPriceFont, placeholder: 'Auto' },
    { label: 'Details (px)', value: detailFont, set: setDetailFont, placeholder: 'Auto' },
  ];

  return (
    <div className="pos-config">
      <section className="pos-section">
        <div className="pos-section-head">
          <h2>Product Settings</h2>
          <p>
            How the Products and Inventory screens list this client&rsquo;s catalogue.
            Saved settings apply to everyone here straight away, including tills already open.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="pos-settings-grid">
            <div className="pos-field pos-theme-field">
              <span>Products shown per page</span>
              <span className="pos-theme-hint pos-theme-input-hint">
                Applies to the Products grid and to Inventory, in both its views
              </span>
              <input
                type="number"
                min={POS_PRODUCT_PAGE_SIZE_MIN}
                max={POS_PRODUCT_PAGE_SIZE_MAX}
                step="1"
                inputMode="numeric"
                className={pageSizeValid ? '' : 'pos-theme-bad'}
                value={pageSize}
                onChange={(event) => setPageSize(event.target.value)}
                disabled={saving}
                placeholder="All products"
                aria-label="Products shown per page"
              />
              {/* Flagged rather than corrected, for the same reason the hex
                  fields are: "1" on the way to "12" is not a mistake yet. */}
              {!pageSizeValid && (
                <span className="pos-theme-bad-note">
                  Enter a whole number between {POS_PRODUCT_PAGE_SIZE_MIN} and {POS_PRODUCT_PAGE_SIZE_MAX}.
                </span>
              )}
              <span className="pos-theme-hint">
                {typed === ''
                  ? 'Blank shows every product on one screen, with no pager — which is what this till did before this setting existed.'
                  : 'Leave it blank to go back to showing every product on one screen.'}
              </span>
            </div>

            {/* CARD SIZE. Both blank keeps the responsive layout (3 across on
                a phone up to 6 on a laptop); a width is each card's minimum — as
                many fit per row as the screen allows, then they stretch to
                fill it. */}
            <div className="pos-field pos-theme-field">
              <span>Product card size</span>
              <span className="pos-theme-hint pos-theme-input-hint">
                Applies to the Products grid and to Inventory&rsquo;s card view
              </span>
              <div className="pos-cardsize-row">
                <label className="pos-cardsize-input">
                  <span>Width (px)</span>
                  <input
                    type="number"
                    min={POS_CARD_WIDTH_MIN}
                    max={POS_CARD_WIDTH_MAX}
                    step="1"
                    inputMode="numeric"
                    className={widthValid ? '' : 'pos-theme-bad'}
                    value={cardWidth}
                    onChange={(event) => setCardWidth(event.target.value)}
                    disabled={saving}
                    placeholder="Auto"
                  />
                </label>
                <label className="pos-cardsize-input">
                  <span>Height (px)</span>
                  <input
                    type="number"
                    min={POS_CARD_HEIGHT_MIN}
                    max={POS_CARD_HEIGHT_MAX}
                    step="1"
                    inputMode="numeric"
                    className={heightValid ? '' : 'pos-theme-bad'}
                    value={cardHeight}
                    onChange={(event) => setCardHeight(event.target.value)}
                    disabled={saving}
                    placeholder="Auto"
                  />
                </label>
              </div>
              {(!widthValid || !heightValid) && (
                <span className="pos-theme-bad-note">
                  Width must be {POS_CARD_WIDTH_MIN}&ndash;{POS_CARD_WIDTH_MAX} and height
                  {' '}{POS_CARD_HEIGHT_MIN}&ndash;{POS_CARD_HEIGHT_MAX} pixels.
                </span>
              )}
              <span className="pos-theme-hint">
                Leave either blank for automatic. The width is a minimum: as many cards as fit
                at that width go on each row, then they stretch to fill it.
              </span>
            </div>

            {/* CARD FONT SIZES, one per kind of text on a card, each blank by
                default so the stylesheet's sizes (and their smaller phone
                sizes) still apply until someone chooses otherwise. */}
            <div className="pos-field pos-theme-field">
              <span>Product card font sizes</span>
              <span className="pos-theme-hint pos-theme-input-hint">
                Text on the Products grid and on Inventory&rsquo;s cards
              </span>
              <div className="pos-cardsize-row">
                {fontFields.map((field) => {
                  const ok = parsePosCardDimension(field.value, POS_CARD_FONT_MIN, POS_CARD_FONT_MAX).ok;
                  return (
                    <label key={field.label} className="pos-cardsize-input">
                      <span>{field.label}</span>
                      <input
                        type="number"
                        min={POS_CARD_FONT_MIN}
                        max={POS_CARD_FONT_MAX}
                        step="1"
                        inputMode="numeric"
                        className={ok ? '' : 'pos-theme-bad'}
                        value={field.value}
                        onChange={(event) => field.set(event.target.value)}
                        disabled={saving}
                        placeholder={field.placeholder}
                      />
                    </label>
                  );
                })}
              </div>
              {!fontValid && (
                <span className="pos-theme-bad-note">
                  Font sizes must be {POS_CARD_FONT_MIN}&ndash;{POS_CARD_FONT_MAX} pixels.
                </span>
              )}
              <span className="pos-theme-hint">
                Details covers the description, stock count and the small tags. Leave a field
                blank for the default size.
              </span>
            </div>
          </div>

          <div className="pos-theme-actions">
            <button type="button" className="pos-btn pos-btn-ghost" onClick={handleReset} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="pos-btn pos-btn-primary" disabled={saving || !valid}>
              {saving ? 'Saving…' : 'Save Configuration'}
            </button>
          </div>
        </form>
      </section>

      <Modal
        isOpen={Boolean(modal)}
        title={modal?.title || ''}
        message={modal?.message || ''}
        type={modal?.type || 'info'}
        confirmText="OK"
        onClose={() => setModal(null)}
      />
    </div>
  );
}

export default PosProductSettingsForm;
